import React from 'react'
import PageTitle from "../components/subcomponents/PageTitle";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import {TablePaper} from "../components/TablePaper";
import {InnerPaperContainer} from "../components/subcomponents/Containers/Containers";
import BillingConfigForm, {BillingConfigFormValues} from "../components/forms/BillingConfigForm";
import PageFooter from "../components/subcomponents/Footer/PageFooter";
import ConfigService, {ConfigValidationError} from '../services/ConfigService'
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import {styled} from "@mui/material/styles";
import {FormIds} from "../constants";
import {Config, DbCustomer, patientHashExcelFilename} from "sharedlibs";
import {useTranslation} from "react-i18next";
import {getCustomers, downloadPatientHashExcel} from "../api/customers";
import {triggerMonthlyProductExport, triggerLowImplantsReport, triggerTmzDailyReport} from "../api/software";
import {updateConfig} from "../api/config";
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {useUserRole} from "../utils/UserRoleContext";
import {logBusinessAudit} from "../utils/auditLogHelper";
import {filenameFromContentDisposition} from "../utils/helperFunctions";
import {z} from 'zod';

const emailSchema = z.string().email()
const isValidEmail = (s: string) => emailSchema.safeParse(s).success

const FooterPlaceholder = styled("span")({
  height: "3rem",
  display: "block"
})

// ---------------------------------------------------------------------------
// Shared recipient-tab hook — encapsulates list state and basic mutations
// ---------------------------------------------------------------------------

interface RecipientItem {
  id: number
  email: string
}

function useRecipientTab(nextId: React.MutableRefObject<number>) {
  const [recipients, setRecipients] = React.useState<RecipientItem[]>([])
  const [dirty, setDirty] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [testing, setTesting] = React.useState(false)

  function init(emails: string[]) {
    setRecipients(emails.map(email => ({id: nextId.current++, email})))
    setDirty(false)
  }

  function update(id: number, value: string) {
    setRecipients(prev => prev.map(r => r.id === id ? {...r, email: value} : r))
    setDirty(true)
  }

  function remove(id: number) {
    setRecipients(prev => prev.filter(r => r.id !== id))
    setDirty(true)
  }

  function add() {
    setRecipients(prev => [...prev, {id: ++nextId.current, email: ''}])
    setDirty(true)
  }

  function getRawTrimmed(): string[] {
    return Array.from(new Set(recipients.map(r => r.email.trim()).filter(r => r.length > 0)))
  }

  function commit(emails: string[]) {
    setRecipients(emails.map(email => ({id: nextId.current++, email})))
    setDirty(false)
  }

  return {recipients, dirty, saving, setSaving, testing, setTesting, init, update, remove, add, getRawTrimmed, commit}
}

// ---------------------------------------------------------------------------
// Shared recipient-tab UI component
// ---------------------------------------------------------------------------

interface ReportRecipientsTabProps {
  title: string
  description: string
  tab: ReturnType<typeof useRecipientTab>
  onSave: () => void
  onTest: () => void
}

function ReportRecipientsTab({title, description, tab, onSave, onTest}: ReportRecipientsTabProps) {
  const {t} = useTranslation()
  return (
    <TablePaper>
      <InnerPaperContainer>
        <Typography variant="h4">{title}</Typography>
      </InnerPaperContainer>
      <Box sx={{padding: '1rem'}}>
        {tab.recipients.length === 0 && (
          <Alert severity="warning" sx={{mb: 2}}>
            {t('pages.configPage.reports.noRecipientsWarning')}
          </Alert>
        )}
        <Stack spacing={1} sx={{mb: 2}}>
          {tab.recipients.map((item) => {
            const trimmed = item.email.trim()
            const invalid = trimmed.length > 0 && !isValidEmail(trimmed)
            return (
              <Stack key={item.id} direction="row" spacing={1} sx={{alignItems: 'center'}}>
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  placeholder={t('pages.configPage.reports.emailPlaceholder')}
                  value={item.email}
                  error={invalid}
                  onChange={(e) => tab.update(item.id, e.target.value)}
                />
                <IconButton
                  aria-label={t('pages.configPage.reports.remove')}
                  onClick={() => tab.remove(item.id)}
                >
                  <DeleteOutlineIcon/>
                </IconButton>
              </Stack>
            )
          })}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={tab.add}>
            {t('pages.configPage.reports.addEmail')}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!tab.dirty || tab.saving}
          >
            {t('pages.configPage.reports.save')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onTest}
            disabled={tab.testing}
          >
            {t('pages.configPage.reports.test')}
          </Button>
        </Stack>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{mt: 3, textAlign: 'left'}}>
            {description}
          </Typography>
        )}
      </Box>
    </TablePaper>
  )
}

// ---------------------------------------------------------------------------
// ConfigPage
// ---------------------------------------------------------------------------

export default function ConfigPage() {

  const {isSuperAdmin} = useUserRole()

  const [config, setConfig] = React.useState<Config | null>(null)
  const [formDirty, setFormDirty] = React.useState<boolean>(false)
  // Bumped after a billing save: remounts the form on the config as stored.
  const [billingFormKey, setBillingFormKey] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)
  const [tabValue, setTabValue] = React.useState(0)

  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState("")
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success")

  const [customers, setCustomers] = React.useState<DbCustomer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string>('')
  const [selectedExportDate, setSelectedExportDate] = React.useState<Dayjs>(dayjs())
  const [patientExportLoading, setPatientExportLoading] = React.useState(false)

  const nextRecipientId = React.useRef(0)
  const monthly = useRecipientTab(nextRecipientId)
  const lowImplants = useRecipientTab(nextRecipientId)
  const tmzDaily = useRecipientTab(nextRecipientId)

  const {t} = useTranslation()

  function showSuccess(message: string) {
    setSnackbarSeverity('success')
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  function showError(message: string) {
    setSnackbarSeverity('error')
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  function showWarning(message: string) {
    setSnackbarSeverity('warning')
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  function validateEmails(tab: ReturnType<typeof useRecipientTab>): string[] | null {
    const trimmed = tab.getRawTrimmed()
    for (const r of trimmed) {
      if (!isValidEmail(r)) {
        showError(t('pages.configPage.reports.invalidEmail', {email: r}))
        return null
      }
    }
    return trimmed
  }

  async function handleReportSave(
    tab: ReturnType<typeof useRecipientTab>,
    configKey: keyof Config,
    savedMsg: string,
    saveFailedMsg: string,
    auditKey: string,
  ) {
    if (!config) {
      showError(t('activities.dataFetchFail'))
      return
    }
    const trimmed = validateEmails(tab)
    if (!trimmed) return
    const beforeRecipients = (config[configKey] as string[] | undefined) ?? []
    tab.setSaving(true)
    try {
      const newConfig: Config = {...config, [configKey]: trimmed} as Config
      setConfig(await updateConfig(newConfig))
      tab.commit(trimmed)
      showSuccess(savedMsg)
    } catch (e: any) {
      console.error(e)
      showError(saveFailedMsg)
      await reloadConfigAfterFailedSave()
      return
    } finally {
      tab.setSaving(false)
    }
    try {
      await logBusinessAudit(auditKey, '', beforeRecipients, trimmed)
    } catch (e: any) {
      console.error('Audit log failed after successful save:', e)
    }
  }

  /** `triggerFn` sends the test mail; it returns a warning when the mail went out incomplete. */
  async function handleReportTest(
    tab: ReturnType<typeof useRecipientTab>,
    triggerFn: (recipients: string[]) => Promise<string | void>,
    testSentMsg: string,
    testFailedMsg: string,
  ) {
    const trimmed = validateEmails(tab)
    if (!trimmed) return
    if (trimmed.length === 0) {
      showError(t('pages.configPage.reports.emptyRecipients'))
      return
    }
    tab.setTesting(true)
    try {
      const warning = await triggerFn(trimmed)
      if (warning) {
        showWarning(warning)
      } else {
        showSuccess(testSentMsg)
      }
    } catch (e: any) {
      console.error(e)
      showError(testFailedMsg + ' ' + (e.message ?? ''))
    } finally {
      tab.setTesting(false)
    }
  }

  // A save that failed may still have reached the server, e.g. when the connection
  // dropped after the write. Every save sends the whole config, so the page must
  // not go on sending the values from before that save: it reads the stored config
  // again. The form keeps what the user typed, for a retry. If the read fails too,
  // the page drops the config, and every save refuses until the page is reloaded.
  async function reloadConfigAfterFailedSave() {
    try {
      setConfig(await ConfigService.getConfig())
    } catch (e) {
      console.error('Could not read the config again after a failed save:', e)
      setConfig(null)
      setError(true)
    }
  }

  async function onBillingFormSubmit(billingFormData: BillingConfigFormValues) {
    if (!config) {
      setShowSnackbar(true)
      setSnackbarSeverity("error")
      setSnackbarMessage("Could not fetch current config in order to update the config.")
      return
    }

    let stored: Config
    try {
      stored = await ConfigService.updateConfig(billingFormData, config)
    } catch (e) {
      console.error(e)
      if (e instanceof ConfigValidationError) {
        // Nothing was sent, so the config in state is still the stored one.
        showError(e.message)
        return
      }
      showError(t('activities.failedSaving'))
      await reloadConfigAfterFailedSave()
      return
    }

    // Keep the server's copy: a later recipient save spreads it, so it must not
    // write the previous values back. The form remounts on it.
    setConfig(stored)
    setBillingFormKey(key => key + 1)
    showSuccess(t('activities.successfulSaving'))

    try {
      await logBusinessAudit('audit.config.updated', '', config, stored)
    } catch (e: any) {
      console.error('Audit log failed after successful save:', e)
    }
  }

  function getDefaultBillingFormValues(): BillingConfigFormValues {
    const defaultValue = {
      semdatexFixedCosts: "0",
      getemedFixedCosts: "0",
      getemedHotlinePrice: "0",
      semdatexExternalSensorsRevenueShare: "0",
      getemedExternalSensorsRevenueShare: "0",
      biotronikExternalSensorsRevenueShare: "0",
      DocCirrusExternalSensorsRevenueShare: "0",
      semdatexImplantRevenueShare: "0",
      biotronikImplantRevenueShare: "0",
      DocCirrusImplantRevenueShare: "0",
      sapMaterialExtSensors: "",
      sapMaterialExtSensorsGetemed: "",
      sapMaterialImplantsGba: "",
      sapMaterialImplantsNonGba: "",
    }

    return config === null ? defaultValue : ConfigService.mapConfigToBillingConfigForm(config)
  }

  async function loadData() {
    try {
      const config = await ConfigService.getConfig()
      setConfig(config)
      monthly.init(config.monthlyProductExportRecipients ?? [])
      lowImplants.init(config.lowImplantsReportRecipients ?? [])
      tmzDaily.init(config.tmzDailyReportRecipients ?? [])
    } catch (e) {
      console.error(e)
      setError(true)
    }
    setLoading(false)
  }

  React.useEffect(() => {
    loadData().then()
    if (isSuperAdmin) {
      getCustomers().then(setCustomers).catch(console.error)
    }
  }, [isSuperAdmin])

  async function handlePatientExportDownload() {
    if (!selectedCustomerId) {
      setSnackbarSeverity("error")
      setSnackbarMessage(t('pages.configPage.patientExport.errorNoCustomer'))
      setShowSnackbar(true)
      return
    }

    setPatientExportLoading(true)
    try {
      const date = selectedExportDate.format('YYYY-MM-DD')
      const response = await downloadPatientHashExcel(selectedCustomerId, date)
      const blob = await response.blob()

      // The server names the file from its customer record (see patientHashExcelFilename)
      const filename = filenameFromContentDisposition(response.headers.get('Content-Disposition'))
        ?? patientHashExcelFilename(undefined, date)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setSnackbarSeverity("error")
      setSnackbarMessage(t('pages.configPage.patientExport.errorGeneric') + ' ' + (e.message ?? ''))
      setShowSnackbar(true)
    } finally {
      setPatientExportLoading(false)
    }
  }

  function renderBillingForm() {
    return error ?
      <ErrorExclamation message={t('activities.dataFetchFail')}/> :
      <BillingConfigForm key={billingFormKey} onSubmit={onBillingFormSubmit}
                         defaultValues={getDefaultBillingFormValues()} updateDirty={setFormDirty}/>
  }

  function renderPatientExportTab() {
    return (
      <TablePaper>
        <InnerPaperContainer>
          <Typography variant="h4">{t('pages.configPage.title.patientExport')}</Typography>
        </InnerPaperContainer>
        <Box sx={{padding: "1rem", display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap'}}>
          <Select
            value={selectedCustomerId}
            onChange={(e: SelectChangeEvent) => setSelectedCustomerId(e.target.value)}
            displayEmpty
            size="small"
            sx={{minWidth: 220}}
          >
            <MenuItem value="" disabled>{t('pages.configPage.patientExport.selectCustomer')}</MenuItem>
            {customers.map(c => (
              <MenuItem key={c.id} value={c.id ?? ''}>{c.name}</MenuItem>
            ))}
          </Select>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('pages.configPage.patientExport.date')}
              disableFuture
              format={'DD/MM/YYYY'}
              value={selectedExportDate}
              onChange={(v) => setSelectedExportDate(v || dayjs())}
              slotProps={{textField: {size: 'small'}}}
            />
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handlePatientExportDownload}
            disabled={patientExportLoading}
          >
            {patientExportLoading
              ? t('pages.configPage.patientExport.downloading')
              : t('pages.configPage.patientExport.download')}
          </Button>
        </Box>
      </TablePaper>
    )
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('pages.configPage.title.main')}</PageTitle>
      <Divider/>

      {isSuperAdmin && (
        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label={t('pages.configPage.title.invoice')} value={0}/>
            <Tab label={t('pages.configPage.title.patientExport')} value={1}/>
            <Tab label={t('pages.configPage.title.reports')} value={2}/>
          </Tabs>
        </Box>
      )}

      {tabValue === 0 && (
        <TablePaper>
          <InnerPaperContainer>
            <Typography variant="h4">{t('pages.configPage.title.invoice')}</Typography>
          </InnerPaperContainer>
          <Box sx={{padding: "1rem"}}>
            {loading ? <CircularProgress/> : renderBillingForm()}
          </Box>
        </TablePaper>
      )}

      {tabValue === 1 && isSuperAdmin && renderPatientExportTab()}

      {tabValue === 2 && isSuperAdmin && (
        <Stack spacing={2}>
          <ReportRecipientsTab
            title={t('pages.configPage.title.monthlyProductExport')}
            description={t('pages.configPage.monthlyProductExport.description')}
            tab={monthly}
            onSave={() => handleReportSave(monthly, 'monthlyProductExportRecipients',
              t('pages.configPage.monthlyProductExport.saved'),
              t('pages.configPage.monthlyProductExport.saveFailed'),
              'audit.config.monthlyExportRecipientsUpdated')}
            onTest={() => handleReportTest(monthly,
              async recipients => (await triggerMonthlyProductExport(recipients)).sapSheetMissing
                ? t('pages.configPage.monthlyProductExport.testSentWithoutSapSheet')
                : undefined,
              t('pages.configPage.monthlyProductExport.testSent'),
              t('pages.configPage.monthlyProductExport.testFailed'))}
          />
          <ReportRecipientsTab
            title={t('pages.configPage.title.lowImplantsReport')}
            description={t('pages.configPage.lowImplantsReport.description')}
            tab={lowImplants}
            onSave={() => handleReportSave(lowImplants, 'lowImplantsReportRecipients',
              t('pages.configPage.lowImplantsReport.saved'),
              t('pages.configPage.lowImplantsReport.saveFailed'),
              'audit.config.lowImplantsRecipientsUpdated')}
            onTest={() => handleReportTest(lowImplants, triggerLowImplantsReport,
              t('pages.configPage.lowImplantsReport.testSent'),
              t('pages.configPage.lowImplantsReport.testFailed'))}
          />
          <ReportRecipientsTab
            title={t('pages.configPage.title.tmzDailyReport')}
            description={t('pages.configPage.tmzDailyReport.description')}
            tab={tmzDaily}
            onSave={() => handleReportSave(tmzDaily, 'tmzDailyReportRecipients',
              t('pages.configPage.tmzDailyReport.saved'),
              t('pages.configPage.tmzDailyReport.saveFailed'),
              'audit.config.tmzDailyRecipientsUpdated')}
            onTest={() => handleReportTest(tmzDaily, triggerTmzDailyReport,
              t('pages.configPage.tmzDailyReport.testSent'),
              t('pages.configPage.tmzDailyReport.testFailed'))}
          />
        </Stack>
      )}

      <FooterPlaceholder/>
      {tabValue === 0 && (
        <PageFooter>
          <Button onClick={() => {
            window.location.reload()
          }} variant="contained" color="inherit">
            {t('activities.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form={FormIds.billingConfig}
            disabled={!formDirty}
          >
            {t('activities.save')}
          </Button>
        </PageFooter>
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={6000}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity as "success" | "warning" | "error"}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  )
}
